import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from '@/lib/supabase';
import { roleDefs } from '@/data/roleDefs';
import { ttlFromExpiry } from '@/lib/invite';
import type {
  AvatarTone,
  Certificate,
  Course,
  Enrollment,
  Invite,
  Lesson,
  OrgNode,
  Profile,
  Question,
  QuizQuestion,
  Role,
  RoleDef,
  RosterEntry,
  TestReview,
  SurveySummary,
  ItemStat,
  UserStatus,
} from '@/types/domain';
import type { CertificateVerification, CreateInviteInput, DataRepository } from './types';

/**
 * Supabase-backed repository — the production data path.
 *
 * Every method returns the SAME domain types the mock returns, so screens
 * never know which backend is live. Rows come back snake_case and untyped
 * (the generated `database.ts` is a placeholder); we map them to the
 * camelCase UI shapes at this edge. Regenerate `database.ts` for full
 * type-safety once convenient — the mappers below are the contract.
 */

type Row = Record<string, unknown>;
// `database.ts` is still a placeholder (no table/function types), so the typed
// client rejects writes/rpc. Use a loose client until real types are generated
// at deploy (`supabase gen types`), then drop this cast for full type-safety.
const s = (): SupabaseClient => getSupabase() as unknown as SupabaseClient;

/* ── Row → domain mappers ─────────────────────────────────── */
const str = (v: unknown, fallback = ''): string => (v == null ? fallback : String(v));
const initials = (first: unknown, last: unknown) =>
  (str(first).charAt(0) + str(last).charAt(0)).toUpperCase();

function mapProfile(r: Row): Profile {
  return {
    id: str(r.id),
    title: str(r.title),
    firstEn: str(r.first_en),
    lastEn: str(r.last_en),
    firstTh: str(r.first_th),
    lastTh: str(r.last_th),
    role: r.role as Role,
    status: r.status as UserStatus,
    deptId: str(r.dept_id),
    unitId: str(r.unit_id),
    profession: (r.profession as Profile['profession']) ?? undefined,
    titlePrefix: r.title_prefix ? str(r.title_prefix) : undefined,
    titleSuffix: r.title_suffix ? str(r.title_suffix) : undefined,
    employeeId: str(r.employee_id),
    email: str(r.email),
    phone: r.phone ? str(r.phone) : undefined,
    telegramUsername: r.telegram_username ? str(r.telegram_username) : undefined,
    photoUrl: r.photo_url ? str(r.photo_url) : undefined,
    initials: initials(r.first_en, r.last_en) || 'WP',
    tone: (str(r.tone, 'sage') as AvatarTone),
    lastActive: r.last_active ? str(r.last_active) : undefined,
    onboarded: r.onboarded === true,
  };
}

function mapLesson(r: Row, doneLessonIds = new Set<string>()): Lesson {
  const id = str(r.id);
  return {
    id,
    order: Number(r.ord ?? 0),
    title: { en: str(r.title_en), th: str(r.title_th) },
    kind: (r.kind as Lesson['kind']) ?? 'video',
    minutes: Number(r.minutes ?? 0),
    done: doneLessonIds.has(id) || r.done === true,
    content: r.content ?? null,
  };
}

function mapCourse(r: Row, doneLessonIds = new Set<string>()): Course {
  const lessons = Array.isArray(r.lessons) ? (r.lessons as Row[]).map((lesson) => mapLesson(lesson, doneLessonIds)) : [];
  lessons.sort((a, b) => a.order - b.order);
  return {
    id: str(r.id),
    code: str(r.code),
    title: { en: str(r.title_en), th: str(r.title_th) },
    host: str(r.host),
    type: r.type as Course['type'],
    status: r.status as Course['status'],
    cover: str(r.cover, '#e6e1d6'),
    coverUrl: r.cover_url ? str(r.cover_url) : undefined,
    logoUrl: r.logo_url ? str(r.logo_url) : undefined,
    hours: Number(r.hours ?? 0),
    capacity: Number(r.capacity ?? 0),
    enrolled: Number(r.enrolled ?? 0),
    passScore: Number(r.pass_score ?? 80),
    certYears: Number(r.cert_valid_years ?? 1),
    summary: str(r.summary),
    requirements: (r.requirements as Course['requirements']) ?? [],
    shuffleQuestions: r.shuffle_questions === true,
    shuffleChoices: r.shuffle_choices === true,
    pretestRevealAnswers: r.pretest_reveal_answers === true,
    lessons,
    enrollStart: r.enroll_start ? str(r.enroll_start) : undefined,
    enrollEnd: r.enroll_end ? str(r.enroll_end) : undefined,
    seminar: r.seminar ? str(r.seminar) : null,
    seminarDate: r.seminar_date ? str(r.seminar_date) : undefined,
    venue: r.venue ? str(r.venue) : null,
  };
}

async function loadDoneLessonIds(rows: Row[]): Promise<Set<string>> {
  const lessonIds = rows.flatMap((r) => (Array.isArray(r.lessons) ? (r.lessons as Row[]).map((l) => str(l.id)) : []));
  if (lessonIds.length === 0) return new Set();

  const { data: auth } = await s().auth.getUser();
  const uid = auth.user?.id;
  if (!uid) return new Set();

  const { data, error } = await s().from('lesson_progress').select('lesson_id').eq('user_id', uid).in('lesson_id', lessonIds);
  if (error) throw error;
  return new Set(((data as Row[]) ?? []).map((r) => str(r.lesson_id)));
}

function mapEnrollment(r: Row): Enrollment {
  return {
    courseId: str(r.course_id),
    progress: Number(r.progress ?? 0),
    attended: (r.attended as boolean | null) ?? null,
    pretest: r.pretest == null ? null : Number(r.pretest),
    posttest: r.posttest == null ? null : Number(r.posttest),
    eval: (r.eval_done as boolean) ?? null,
    status: (r.status as Enrollment['status']) ?? 'enrolled',
  };
}

function mapCertificate(r: Row): Certificate {
  return {
    id: str(r.id),
    course: str(r.course_name),
    th: str(r.course_name_th),
    host: str(r.host),
    issued: str(r.issued_on),
    expires: str(r.expires_on),
    status: r.status as Certificate['status'],
    instructor: str(r.instructor),
  };
}

function mapInvite(r: Row): Invite {
  const dept = r.departments as Row | null;
  const unit = r.units as Row | null;
  return {
    id: str(r.id),
    token: str(r.token),
    role: r.role as Role,
    profession: r.profession as Invite['profession'],
    dept: dept ? str(dept.name_en) : str(r.dept_id),
    unit: unit ? str(unit.name) : str(r.unit_id),
    maxUses: Number(r.max_uses ?? 1),
    uses: Number(r.uses ?? 0),
    expiresAt: str(r.expires_at),
    status: r.status as Invite['status'],
    createdBy: str(r.created_by),
    createdAt: str(r.created_at),
  };
}

function mapQuestion(r: Row): Question {
  const bank = r.question_banks as Row | null;
  return {
    id: str(r.id),
    type: r.type as Question['type'],
    bank: bank ? str(bank.name) : str(r.bank_id),
    stem: str(r.stem),
    image: !!r.image_url,
    options: (r.options as string[]) ?? [],
    answer: r.answer as number | number[],
    testKind: (r.test_kind as Question['testKind']) ?? undefined,
    rationale: r.rationale ? str(r.rationale) : undefined,
  };
}

function mapQuizQuestion(r: Row): QuizQuestion {
  return {
    id: str(r.id),
    type: r.type as QuizQuestion['type'],
    stem: str(r.stem),
    imageUrl: r.image_url ? str(r.image_url) : undefined,
    options: (r.options as string[]) ?? [],
    points: Number(r.points ?? 1),
  };
}

async function currentUserId(): Promise<string> {
  const { data } = await s().auth.getUser();
  if (!data.user) throw new Error('Not authenticated');
  return data.user.id;
}

export const supabaseRepository: DataRepository = {
  /* ── Catalog ── */
  async listCourses() {
    const { data, error } = await s().from('courses').select('*, lessons(*)').neq('status', 'hidden').order('code');
    if (error) throw error;
    const rows = data as Row[];
    const doneLessonIds = await loadDoneLessonIds(rows);
    return rows.map((r) => mapCourse(r, doneLessonIds));
  },
  async getCourse(id) {
    const { data, error } = await s().from('courses').select('*, lessons(*)').eq('id', id).maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const rows = [data as Row];
    const doneLessonIds = await loadDoneLessonIds(rows);
    return mapCourse(rows[0], doneLessonIds);
  },
  async updateCourse(id, patch) {
    const row: Record<string, unknown> = {};
    if (patch.host !== undefined) row.host = patch.host;
    if (patch.passScore !== undefined) row.pass_score = patch.passScore;
    if (patch.shuffleQuestions !== undefined) row.shuffle_questions = patch.shuffleQuestions;
    if (patch.shuffleChoices !== undefined) row.shuffle_choices = patch.shuffleChoices;
    if (patch.pretestRevealAnswers !== undefined) row.pretest_reveal_answers = patch.pretestRevealAnswers;

    if (Object.keys(row).length === 0) {
      const { data, error } = await s().from('courses').select('*, lessons(*)').eq('id', id).single();
      if (error) throw error;
      return mapCourse(data as Row);
    }

    const { data, error } = await s().from('courses').update(row).eq('id', id).select('*, lessons(*)').single();
    if (error) throw error;
    return mapCourse(data as Row);
  },
  async updateLesson() {
    // TODO(deploy): map camelCase patch → lesson columns. See PUNCHLIST D1.
    throw new Error('updateLesson is not wired to Supabase yet (mock-only).');
  },

  /* ── Learner ── */
  async getMe() {
    const { data: authData } = await s().auth.getUser();
    const authUser = authData.user;
    if (!authUser) throw new Error('Not authenticated');
    const { data, error } = await s().from('profiles').select('*').eq('id', authUser.id).single();
    if (error) throw error;
    const profile = mapProfile(data as Row);
    // LINE display name (captured at signup) — used for greetings.
    const lineName = (authUser.user_metadata as Record<string, unknown> | null)?.line_display_name;
    return typeof lineName === 'string' && lineName.trim() ? { ...profile, lineName } : profile;
  },
  async completeProfile(input) {
    const { error } = await s().rpc('complete_profile', {
      p_first_en: input.firstEn,
      p_last_en: input.lastEn,
      p_first_th: input.firstTh,
      p_last_th: input.lastTh,
      p_prefix: input.prefix,
      p_suffix: input.suffix ?? null,
      p_photo_url: input.photoUrl ?? null,
    });
    if (error) {
      console.error('[complete_profile] failed', error);
      throw error;
    }
  },
  async listMyEnrollments() {
    // MUST filter by the current user: instructor/admin/superadmin have a broad
    // SELECT on enrollments (for rosters), so relying on RLS alone would return
    // EVERYONE's enrollments here and leak others' "My learning".
    const uid = await currentUserId();
    const { data, error } = await s().from('enrollments').select('*').eq('user_id', uid);
    if (error) throw error;
    return (data as Row[]).map(mapEnrollment);
  },
  async enroll(courseId) {
    const uid = await currentUserId();
    const { data, error } = await s()
      .from('enrollments')
      .upsert({ user_id: uid, course_id: courseId }, { onConflict: 'user_id,course_id' })
      .select()
      .single();
    if (error) throw error;
    return mapEnrollment(data as Row);
  },
  async submitEvaluation(courseId, answers, comment) {
    const uid = await currentUserId();
    const { error: e1 } = await s()
      .from('evaluations')
      .upsert({ user_id: uid, course_id: courseId, answers, comment: comment ?? null }, { onConflict: 'user_id,course_id' });
    if (e1) throw e1;
    const { error: e2 } = await s()
      .from('enrollments')
      .update({ eval_done: true })
      .eq('user_id', uid)
      .eq('course_id', courseId);
    if (e2) throw e2;
  },

  /* ── Assessment (learner-safe — server grades; answer key never sent) ── */
  async getTestQuestions(courseId, kind) {
    const { data, error } = await s().rpc('get_test_questions', { p_course_id: courseId, p_kind: kind ?? null });
    if (error) throw error;
    const rows = data as Row[];
    const questions = rows.map(mapQuizQuestion);
    // Question images live in the PRIVATE `question-media` bucket, so the stored
    // path won't load in <img>. Swap each for a short-lived signed URL.
    await Promise.all(
      rows.map(async (r, i) => {
        const raw = r.image_url ? String(r.image_url) : '';
        if (!raw) return;
        const path = raw.replace(/^question-media\//, '');
        const { data: signed } = await s().storage.from('question-media').createSignedUrl(path, 3600);
        if (signed?.signedUrl) questions[i].imageUrl = signed.signedUrl;
      }),
    );
    return questions;
  },
  async submitTest(courseId, kind, answers) {
    const { data, error } = await s().rpc('submit_test', { p_course_id: courseId, p_kind: kind, p_answers: answers });
    if (error) throw error;
    const r = data as Row;
    return {
      score: Number(r.score ?? 0),
      passScore: Number(r.passScore ?? 80),
      passed: r.passed === true,
      reveal: r.reveal === true,
      key: (r.key as Record<string, number | number[]> | null) ?? null,
      rationale: (r.rationale as Record<string, string> | null) ?? null,
      cooldownUntil: (r.cooldownUntil as string | null) ?? null,
    };
  },
  async getTestDraft(courseId, kind) {
    const { data, error } = await s()
      .from('test_drafts')
      .select('answers, q_index, seed')
      .eq('course_id', courseId)
      .eq('kind', kind)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const r = data as Row;
    return {
      answers: (r.answers as Record<string, number | number[]>) ?? {},
      i: Number(r.q_index ?? 0),
      seed: r.seed != null ? Number(r.seed) : null,
    };
  },
  async saveTestDraft(courseId, kind, draft) {
    const uid = await currentUserId();
    const { error } = await s().from('test_drafts').upsert(
      { user_id: uid, course_id: courseId, kind, answers: draft.answers, q_index: draft.i, seed: draft.seed, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,course_id,kind' },
    );
    if (error) throw error;
  },
  async clearTestDraft(courseId, kind) {
    const { error } = await s().from('test_drafts').delete().eq('course_id', courseId).eq('kind', kind);
    if (error) throw error;
  },
  async getSurveySummary(courseId) {
    const { data, error } = await s().rpc('get_survey_summary', { p_course_id: courseId });
    if (error) throw error;
    return data as SurveySummary;
  },
  async getItemAnalysis(courseId, kind) {
    const { data, error } = await s().rpc('get_item_analysis', { p_course_id: courseId, p_kind: kind });
    if (error) throw error;
    return (data ?? []) as ItemStat[];
  },
  async getTestReview(courseId, kind) {
    const { data, error } = await s().rpc('get_test_review', { p_course_id: courseId, p_kind: kind });
    if (error) throw error;
    const review = data as TestReview;
    // EKG images live in the private question-media bucket → sign them for display.
    await Promise.all(
      review.questions.map(async (q) => {
        if (!q.image) return;
        const path = String(q.image).replace(/^question-media\//, '');
        const { data: signed } = await s().storage.from('question-media').createSignedUrl(path, 3600);
        q.image = signed?.signedUrl ?? undefined;
      }),
    );
    return review;
  },
  async getLessonQuiz(lessonId) {
    const { data, error } = await s().rpc('get_lesson_quiz', { p_lesson_id: lessonId });
    if (error) throw error;
    // Formative check: the RPC returns the answer key so the next-lesson gate can
    // reveal on-device. (The graded post-test RPC must NOT — see getTestQuestions.)
    return (data as Row[]).map((r) => ({
      ...mapQuizQuestion(r),
      answer: (r.answer ?? r.answer_key) as number | number[],
    }));
  },
  async submitLessonQuiz(lessonId, answers) {
    const { data, error } = await s().rpc('submit_lesson_quiz', { p_lesson_id: lessonId, p_answers: answers });
    if (error) throw error;
    const r = data as Row;
    return {
      score: Number(r.score ?? 0),
      correct: Number(r.correct ?? 0),
      total: Number(r.total ?? 0),
      reveal: r.reveal === true,
      key: (r.key as Record<string, number | number[]> | null) ?? null,
    };
  },
  async completeLesson(lessonId) {
    const { error } = await s().rpc('complete_lesson', { p_lesson_id: lessonId });
    if (error) throw error;
  },

  /* ── Certificates ── */
  async listCertificates() {
    // Own wallet only — the read policy also allows instructors/admins to see
    // all certificates, so filter by the current user explicitly.
    const uid = await currentUserId();
    const { data, error } = await s().from('certificates').select('*').eq('user_id', uid).order('issued_on', { ascending: false });
    if (error) throw error;
    return (data as Row[]).map(mapCertificate);
  },
  async issueCertificate(courseId) {
    // Server (SECURITY DEFINER) re-validates every requirement and writes above RLS
    // (cert_issue is instructor-only). Returns the issued/existing cert, or null when
    // the learner is not yet eligible. Auto-issuance also happens via the enrollments trigger.
    const { data, error } = await s().rpc('issue_certificate', { p_course_id: courseId });
    if (error) throw error;
    return data ? mapCertificate(data as Row) : null;
  },
  async verifyCertificate(code) {
    const { data, error } = await s().rpc('verify_certificate', { p_code: code.trim() });
    if (error) throw error;
    const rows = (data as Row[]) ?? [];
    if (rows.length === 0) return { result: 'not-found' } as CertificateVerification;
    const r = rows[0];
    const status = r.status as Certificate['status'];
    const result = status === 'revoked' ? 'revoked' : status === 'expired' ? 'expired' : 'valid';
    const certificate: Certificate = {
      id: str(r.code),
      course: str(r.course_en),
      th: str(r.course_th),
      host: str(r.host),
      issued: str(r.issued_on),
      expires: str(r.expires_on),
      status,
      instructor: str(r.holder_en), // holder shown; instructor not exposed by the public RPC
    };
    return { result, certificate };
  },

  /* ── Instructor ── */
  async listRoster(courseId) {
    if (!courseId) return [];
    const { data, error } = await s()
      .from('enrollments')
      .select('*, profiles(first_en,last_en,first_th,last_th,dept_id,unit_id,employee_id,photo_url,tone)')
      .eq('course_id', courseId);
    if (error) throw error;
    return (data as Row[]).map((r): RosterEntry => {
      const p = (r.profiles as Row | null) ?? {};
      const post = r.posttest == null ? null : Number(r.posttest);
      const issued = r.status === 'completed'; // app_issue_certificate flips status on issue
      const ready = !issued && Number(r.progress ?? 0) >= 100 && r.attended === true && post != null && post >= 80 && (r.eval_done as boolean);
      return {
        userId: str(r.user_id),
        name: `${str(p.first_en)} ${str(p.last_en)}`.trim(),
        th: `${str(p.first_th)} ${str(p.last_th)}`.trim(),
        dept: str(p.dept_id),
        unit: str(p.unit_id),
        emp: str(p.employee_id),
        photoUrl: p.photo_url ? str(p.photo_url) : undefined,
        tone: p.tone ? (str(p.tone) as RosterEntry['tone']) : undefined,
        lessons: Number(r.progress ?? 0),
        attended: r.attended === true,
        pretest: r.pretest == null ? null : Number(r.pretest),
        posttest: post,
        eval: (r.eval_done as boolean) ?? false,
        cert: issued ? 'issued' : ready ? 'ready' : 'blocked',
      };
    });
  },
  async markAttendance(courseId, userId) {
    // Direct client writes to enrollments.attended are blocked by guard_enrollment_update;
    // the RPC records the check-in + flips the flag as the system.
    const { error } = await s().rpc('mark_attendance', { p_course_id: courseId, p_user_id: userId ?? null });
    if (error) throw error;
  },
  async setCheckin(courseId, open) {
    const { data, error } = await s().rpc('set_checkin', { p_course_id: courseId, p_open: open });
    if (error) throw error;
    const d = (data ?? {}) as { open?: boolean; token?: string };
    return { open: !!d.open, token: str(d.token) };
  },
  async checkIn(courseId, token) {
    const { error } = await s().rpc('check_in', { p_course_id: courseId, p_token: token });
    if (error) throw error;
  },
  async markAttended(courseId, userId) {
    const { error } = await s().rpc('mark_attended', { p_course_id: courseId, p_user_id: userId });
    if (error) throw error;
  },
  async setAttended(courseId, userId, present) {
    const { error } = await s().rpc('set_attended', { p_course_id: courseId, p_user_id: userId, p_present: present });
    if (error) throw error;
  },
  async listQuestions(bankId) {
    let q = s().from('questions').select('*, question_banks(name)').order('ord');
    if (bankId) q = q.eq('bank_id', bankId);
    const { data, error } = await q;
    if (error) throw error;
    const rows = data as Row[];
    const questions = rows.map(mapQuestion);
    // EKG images live in the PRIVATE `question-media` bucket — sign each so the
    // big-screen reveal (and the instructor test viewer) can render them.
    await Promise.all(
      rows.map(async (r, i) => {
        const raw = r.image_url ? String(r.image_url) : '';
        if (!raw) return;
        const path = raw.replace(/^question-media\//, '');
        const { data: signed } = await s().storage.from('question-media').createSignedUrl(path, 3600);
        if (signed?.signedUrl) questions[i].imageUrl = signed.signedUrl;
      }),
    );
    return questions;
  },

  /* ── Admin ── */
  async listInvites() {
    const { data, error } = await s()
      .from('invites')
      .select('*, departments(name_en), units(name)')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data as Row[]).map(mapInvite);
  },
  async createInvite(input: CreateInviteInput) {
    const { data: token, error } = await s().rpc('create_invite', {
      p_role: input.role,
      p_profession: input.profession,
      p_dept_id: input.dept,
      p_unit_id: input.unit || null,
      p_max_uses: input.maxUses,
      p_ttl: ttlFromExpiry(input.expiresAt),
    });
    if (error) throw error;
    const { data, error: e2 } = await s()
      .from('invites')
      .select('*, departments(name_en), units(name)')
      .eq('token', token as string)
      .single();
    if (e2) throw e2;
    return mapInvite(data as Row);
  },
  async revokeInvite(id) {
    const { error } = await s().rpc('revoke_invite', { p_id: id });
    if (error) throw error;
  },
  async listUsers() {
    const { data, error } = await s().from('profiles').select('*').order('created_at');
    if (error) throw error;
    return (data as Row[]).map(mapProfile);
  },
  async updateUserRole(id, role: Role) {
    const { data, error } = await s().from('profiles').update({ role }).eq('id', id).select().single();
    if (error) throw error;
    return mapProfile(data as Row);
  },
  async setUserStatus(id, status: UserStatus) {
    const { data, error } = await s().from('profiles').update({ status }).eq('id', id).select().single();
    if (error) throw error;
    return mapProfile(data as Row);
  },
  async updateMyProfile(patch) {
    // Whitelisted self-edit via SECURITY DEFINER RPC (raw client update could change role/dept).
    const { error } = await s().rpc('update_my_profile', {
      p_first_en: patch.firstEn, p_last_en: patch.lastEn,
      p_first_th: patch.firstTh, p_last_th: patch.lastTh,
      p_tone: patch.tone ?? null,
    });
    if (error) throw error;
    const uid = await currentUserId();
    const { data, error: e2 } = await s().from('profiles').select('*').eq('id', uid).single();
    if (e2) throw e2;
    return mapProfile(data as Row);
  },
  async adminUpdateProfile(id, patch) {
    // Admin may write any profile column under RLS (profile_admin_write). role/status excluded here.
    const upd: Record<string, unknown> = {
      first_en: patch.firstEn, last_en: patch.lastEn, first_th: patch.firstTh, last_th: patch.lastTh,
    };
    if (patch.tone !== undefined) upd.tone = patch.tone;
    if (patch.titlePrefix !== undefined) { upd.title_prefix = patch.titlePrefix; upd.title = patch.titlePrefix; }
    if (patch.titleSuffix !== undefined) upd.title_suffix = patch.titleSuffix || null;
    if (patch.profession !== undefined) upd.profession = patch.profession;
    if (patch.deptId !== undefined) upd.dept_id = patch.deptId;
    if (patch.unitId !== undefined) upd.unit_id = patch.unitId;
    if (patch.email !== undefined) upd.email = patch.email;
    const { data, error } = await s().from('profiles').update(upd).eq('id', id).select().single();
    if (error) throw error;
    return mapProfile(data as Row);
  },
  async listOrg() {
    const { data, error } = await s().from('departments').select('*, units(id,name)').order('name_en');
    if (error) throw error;
    return (data as Row[]).map((r): OrgNode => ({
      deptId: str(r.id),
      dept: str(r.name_en),
      th: str(r.name_th),
      units: Array.isArray(r.units) ? (r.units as Row[]).map((u) => ({ id: str(u.id), name: str(u.name) })) : [],
      staff: Number(r.staff_count ?? 0),
    }));
  },
  async createDept(input) {
    const { error } = await s().from('departments').insert({ id: crypto.randomUUID().slice(0, 8), name_en: input.nameEn, name_th: input.nameTh });
    if (error) throw error;
  },
  async updateDept(id, patch) {
    const row: Record<string, string> = {};
    if (patch.nameEn !== undefined) row.name_en = patch.nameEn;
    if (patch.nameTh !== undefined) row.name_th = patch.nameTh;
    const { error } = await s().from('departments').update(row).eq('id', id);
    if (error) throw error;
  },
  async deleteDept(id) {
    const { error } = await s().from('departments').delete().eq('id', id);
    if (error) throw error;
  },
  async createUnit(input) {
    const { error } = await s().from('units').insert({ id: crypto.randomUUID().slice(0, 8), dept_id: input.deptId, name: input.name });
    if (error) throw error;
  },
  async updateUnit(id, name) {
    const { error } = await s().from('units').update({ name }).eq('id', id);
    if (error) throw error;
  },
  async deleteUnit(id) {
    const { error } = await s().from('units').delete().eq('id', id);
    if (error) throw error;
  },
  async getRoleDefs() {
    return roleDefs as Record<Role, RoleDef>;
  },
};
